/**
 * Organization Settings Component
 * Handles organization management, member invitations, and role assignments
 */

import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from './auth-context'
import * as orgApi from './api/organizations'
import { uploadOrganizationLogo } from './api/media'

function OrganizationSettings() {
  const {
    currentOrg,
    hasPermission,
    refreshOrganizations,
    userRole,
    organizations,
    loading: authLoading,
  } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isWelcome = searchParams.get('welcome') === 'true'

  const [activeTab, setActiveTab] = useState('overview')
  const [organization, setOrganization] = useState(null)
  const [members, setMembers] = useState([])
  const [pendingInvitations, setPendingInvitations] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Form states
  const [inviteForm, setInviteForm] = useState({
    email: '',
    role: 'field_agent',
    loading: false,
  })

  const [orgForm, setOrgForm] = useState({
    name: '',
    description: '',
    website: '',
    logoUrl: '',
  })
  const [logoUploading, setLogoUploading] = useState(false)
  const logoInputRef = useRef(null)

  // Try to refresh organizations if none are loaded (e.g., after signup redirect)
  useEffect(() => {
    if (!authLoading && !currentOrg && organizations.length === 0) {
      console.log('[OrgSettings] No organizations loaded, refreshing...')
      refreshOrganizations()
    }
  }, [authLoading, currentOrg, organizations.length, refreshOrganizations])

  // Set default tab based on welcome flow
  useEffect(() => {
    if (isWelcome && hasPermission('org_manage_members')) {
      setActiveTab('members')
    }
  }, [isWelcome, hasPermission])

  const loadOrganizationData = useCallback(async () => {
    if (!currentOrg) return

    try {
      setLoading(true)
      setError('')

      // Load organization details
      const orgData = await orgApi.getOrganization(currentOrg.id)
      setOrganization(orgData)
      setOrgForm({
        name: orgData.name || '',
        description: orgData.description || '',
        website: orgData.website || '',
        logoUrl: orgData.logo_url || '',
      })

      // Load members and invitations if user has permission
      if (hasPermission('org_manage_members')) {
        const membersData = await orgApi.getMembers(currentOrg.id)
        setMembers(membersData)

        // Load pending invitations
        try {
          const invitationsData = await orgApi.getInvitations(currentOrg.id)
          setPendingInvitations(invitationsData)
        } catch (invErr) {
          // Invitations endpoint might not be available, ignore
          console.log('Could not load invitations:', invErr)
          setPendingInvitations([])
        }
      }
    } catch (err) {
      console.error('Failed to load organization data:', err)
      setError('Failed to load organization data')
    } finally {
      setLoading(false)
    }
  }, [currentOrg, hasPermission])

  useEffect(() => {
    if (currentOrg) {
      loadOrganizationData()
    }
  }, [currentOrg, loadOrganizationData])

  const handleInviteUser = async (e) => {
    e.preventDefault()
    if (!hasPermission('org_manage_members')) {
      setError('You do not have permission to invite users')
      return
    }

    try {
      setInviteForm((prev) => ({ ...prev, loading: true }))
      setError('')

      const result = await orgApi.inviteMember(currentOrg.id, inviteForm.email, inviteForm.role)

      // Check if it's a pending invitation or an immediate membership
      if (result.status === 'pending') {
        setSuccess(`Invitation sent to ${inviteForm.email}! They'll be added when they sign up.`)
      } else {
        setSuccess('Member added successfully!')
      }

      setInviteForm({ email: '', role: 'field_agent', loading: false })
      await loadOrganizationData()
    } catch (err) {
      console.error('Failed to invite user:', err)
      setError(err.message || 'Failed to add member')
    } finally {
      setInviteForm((prev) => ({ ...prev, loading: false }))
    }
  }

  const handleRevokeInvitation = async (invitationId, email) => {
    if (!hasPermission('org_manage_members')) {
      setError('You do not have permission to manage invitations')
      return
    }

    if (!confirm(`Are you sure you want to revoke the invitation for ${email}?`)) {
      return
    }

    try {
      setError('')
      await orgApi.revokeInvitation(currentOrg.id, invitationId)
      setSuccess('Invitation revoked successfully!')
      await loadOrganizationData()
    } catch (err) {
      console.error('Failed to revoke invitation:', err)
      setError(err.message || 'Failed to revoke invitation')
    }
  }

  const copyInviteLink = (token, email) => {
    const inviteUrl = `${window.location.origin}/signup?invite=${token}`
    const message = `You've been invited to join ${organization?.name || 'our organization'}!\n\nClick here to sign up: ${inviteUrl}\n\nThis link will expire in 7 days.`
    navigator.clipboard.writeText(message)
    setSuccess(`Invitation link for ${email} copied to clipboard!`)
  }

  const handleUpdateRole = async (userId, newRole) => {
    if (!hasPermission('org_manage_members')) {
      setError('You do not have permission to manage members')
      return
    }

    try {
      setError('')
      await orgApi.updateMemberRole(currentOrg.id, userId, newRole)
      setSuccess('Role updated successfully!')
      await loadOrganizationData()
    } catch (err) {
      console.error('Failed to update role:', err)
      setError(err.message || 'Failed to update role')
    }
  }

  const handleRemoveMember = async (userId, memberName) => {
    if (!hasPermission('org_manage_members')) {
      setError('You do not have permission to manage members')
      return
    }

    if (!confirm(`Are you sure you want to remove ${memberName} from the organization?`)) {
      return
    }

    try {
      setError('')
      await orgApi.removeMember(currentOrg.id, userId)
      setSuccess('Member removed successfully!')
      await loadOrganizationData()
    } catch (err) {
      console.error('Failed to remove member:', err)
      setError(err.message || 'Failed to remove member')
    }
  }

  const handleUpdateOrganization = async (e) => {
    e.preventDefault()
    if (!hasPermission('org_manage_settings')) {
      setError('You do not have permission to manage organization settings')
      return
    }

    try {
      setError('')
      await orgApi.updateOrganization(currentOrg.id, {
        name: orgForm.name,
        description: orgForm.description || null,
        website: orgForm.website || null,
        logoUrl: orgForm.logoUrl || null,
      })
      setSuccess('Organization updated successfully!')
      await refreshOrganizations()
      await loadOrganizationData()
    } catch (err) {
      console.error('Failed to update organization:', err)
      setError(err.message || 'Failed to update organization')
    }
  }

  const handleLogoFileUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate image type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file (JPEG, PNG, WebP, or GIF)')
      return
    }

    // Validate size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('Logo image must be under 5MB')
      return
    }

    try {
      setLogoUploading(true)
      setError('')
      const result = await uploadOrganizationLogo(currentOrg.id, file)
      setOrgForm((prev) => ({ ...prev, logoUrl: result.url }))
      setSuccess('Logo uploaded successfully!')
      await loadOrganizationData()
    } catch (err) {
      console.error('Logo upload failed:', err)
      setError(err.message || 'Failed to upload logo')
    } finally {
      setLogoUploading(false)
      if (logoInputRef.current) logoInputRef.current.value = ''
    }
  }

  const handleRemoveLogo = async () => {
    try {
      setError('')
      await orgApi.updateOrganization(currentOrg.id, { logoUrl: '' })
      setOrgForm((prev) => ({ ...prev, logoUrl: '' }))
      setSuccess('Logo removed successfully!')
      await refreshOrganizations()
      await loadOrganizationData()
    } catch (err) {
      setError(err.message || 'Failed to remove logo')
    }
  }

  const getRoleBadgeColor = (role) => {
    const colors = {
      owner: '#dc3545',
      administrator: '#fd7e14',
      designer: '#0d6efd',
      field_agent: '#20c997',
      viewer: '#6c757d', // Legacy support
    }
    return colors[role] || colors.field_agent
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  // Show loading while auth is loading or if we're waiting for organizations to load
  if (authLoading || (organizations.length === 0 && !currentOrg)) {
    return (
      <div className="org-settings">
        <div className="loading">Loading organization...</div>
      </div>
    )
  }

  if (!currentOrg) {
    return (
      <div className="org-settings">
        <div className="no-org">
          <h2>No Organization Selected</h2>
          <p>Please select an organization from your profile to manage settings.</p>
          <button
            onClick={() => navigate('/pricing')}
            style={{
              marginTop: '1rem',
              padding: '0.75rem 1.5rem',
              background: 'linear-gradient(135deg, #0D9488, #0EA5E9)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
            }}>
            Create an Organization
          </button>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="org-settings">
        <div className="loading">Loading organization settings...</div>
      </div>
    )
  }

  const planCode = currentOrg.subscription_plan_code || 'standard'
  const planLimits = orgApi.getPlanLimits(planCode)

  return (
    <div className="org-settings">
      {isWelcome && (
        <div
          className="welcome-banner"
          style={{
            background:
              'linear-gradient(135deg, rgba(106, 128, 232, 0.1), rgba(117, 78, 167, 0.1))',
            border: '1px solid rgba(106, 128, 232, 0.3)',
            borderRadius: '12px',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            textAlign: 'center',
          }}>
          <h2 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>Welcome to Your Organization!</h2>
          <p style={{ margin: '0', color: '#666' }}>
            You're all set up. Now you can invite team members to collaborate on surveys.
          </p>
        </div>
      )}

      <div className="org-header">
        <h1>{organization?.name || 'Organization Settings'}</h1>
        <div className="plan-info">
          <span className="plan-badge">{planCode.toUpperCase()}</span>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="org-tabs">
        <button
          className={activeTab === 'overview' ? 'active' : ''}
          onClick={() => setActiveTab('overview')}>
          Overview
        </button>
        {hasPermission('org_manage_members') && (
          <button
            className={activeTab === 'members' ? 'active' : ''}
            onClick={() => setActiveTab('members')}>
            Members ({members.length})
          </button>
        )}
        {hasPermission('org_manage_settings') && (
          <button
            className={activeTab === 'settings' ? 'active' : ''}
            onClick={() => setActiveTab('settings')}>
            Settings
          </button>
        )}
      </div>

      <div className="tab-content">
        {activeTab === 'overview' && (
          <div className="overview-tab">
            <div className="org-stats">
              <div className="stat-card">
                <h3>Plan</h3>
                <p className="plan-name">
                  <span className="plan-badge">{planCode.toUpperCase()}</span>
                </p>
              </div>
              <div className="stat-card">
                <h3>Members</h3>
                <p>
                  {organization?.members_count || 0} /{' '}
                  {planLimits.max_members === -1 ? '∞' : planLimits.max_members}
                </p>
              </div>
              <div className="stat-card">
                <h3>Surveys</h3>
                <p>
                  {organization?.surveys_count || 0} /{' '}
                  {planLimits.max_surveys === -1 ? '∞' : planLimits.max_surveys}
                </p>
              </div>
            </div>

            <div className="org-info">
              <h3>Organization Information</h3>
              <div className="info-grid">
                <div>
                  <strong>Name:</strong> {organization?.name}
                </div>
                <div>
                  <strong>Created:</strong> {formatDate(organization?.created_at)}
                </div>
                {organization?.description && (
                  <div>
                    <strong>Description:</strong> {organization.description}
                  </div>
                )}
                {organization?.website && (
                  <div>
                    <strong>Website:</strong>
                    <a href={organization.website} target="_blank" rel="noopener noreferrer">
                      {organization.website}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'members' && hasPermission('org_manage_members') && (
          <div className="members-tab">
            <div className="invite-section">
              <h3>Add New Member</h3>
              <p className="invite-hint">
                Enter an email address to invite someone. If they already have an account, they'll
                be added immediately. Otherwise, they'll receive an invitation and be added when
                they sign up.
              </p>
              <form onSubmit={handleInviteUser} className="invite-form">
                <div className="form-group">
                  <input
                    type="email"
                    placeholder="Email address"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm((prev) => ({ ...prev, email: e.target.value }))}
                    required
                  />
                  <select
                    value={inviteForm.role}
                    onChange={(e) => setInviteForm((prev) => ({ ...prev, role: e.target.value }))}>
                    <option value="field_agent">Field Agent</option>
                    <option value="designer">Designer</option>
                    {(userRole === 'owner' || userRole === 'administrator') && (
                      <option value="administrator">Administrator</option>
                    )}
                  </select>
                  <button type="submit" disabled={inviteForm.loading}>
                    {inviteForm.loading ? 'Sending...' : 'Send Invite'}
                  </button>
                </div>
              </form>
            </div>

            {/* Pending Invitations Section */}
            {pendingInvitations.length > 0 && (
              <div className="members-section pending-section">
                <h3>Pending Invitations ({pendingInvitations.length})</h3>
                <p
                  className="invite-hint"
                  style={{ marginBottom: '1rem', fontSize: '0.9rem', color: '#856404' }}>
                  Copy and share the invitation link with the user. Each link is single-use and
                  expires in 7 days.
                </p>
                <div className="members-list">
                  {pendingInvitations.map((invitation) => (
                    <div key={invitation.id} className="member-item pending-item">
                      <div className="member-info">
                        <div className="member-name pending-email">{invitation.email}</div>
                        <div className="member-meta">
                          <span className="pending-badge">Pending</span>
                          {' • '}Invited {formatDate(invitation.created_at)}
                          {invitation.expires_at && (
                            <span> • Expires {formatDate(invitation.expires_at)}</span>
                          )}
                        </div>
                      </div>
                      <div className="member-role">
                        <span
                          className="role-badge"
                          style={{ backgroundColor: getRoleBadgeColor(invitation.role_code) }}>
                          {invitation.role_code}
                        </span>
                      </div>
                      <div className="member-actions">
                        {invitation.token && (
                          <button
                            className="copy-link-btn"
                            onClick={() => copyInviteLink(invitation.token, invitation.email)}
                            style={{
                              padding: '0.25rem 0.75rem',
                              backgroundColor: '#0D9488',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.85rem',
                              marginRight: '0.5rem',
                            }}>
                            📋 Copy Link
                          </button>
                        )}
                        <button
                          className="revoke-btn"
                          onClick={() => handleRevokeInvitation(invitation.id, invitation.email)}>
                          Revoke
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="members-section">
              <h3>Current Members ({members.length})</h3>
              <div className="members-list">
                {members.map((member) => (
                  <div key={member.id} className="member-item">
                    <div className="member-info">
                      <div className="member-name">{member.full_name || member.email}</div>
                      <div className="member-email">{member.email}</div>
                      <div className="member-meta">Joined {formatDate(member.joined_at)}</div>
                    </div>
                    <div className="member-role">
                      <span
                        className="role-badge"
                        style={{ backgroundColor: getRoleBadgeColor(member.role_code) }}>
                        {member.role_code}
                      </span>
                    </div>
                    {member.role_code !== 'owner' &&
                      (userRole === 'owner' || userRole === 'administrator') && (
                        <div className="member-actions">
                          <select
                            value={member.role_code}
                            onChange={(e) => handleUpdateRole(member.user_id, e.target.value)}
                            disabled={member.role_code === 'administrator' && userRole !== 'owner'}>
                            <option value="field_agent">Field Agent</option>
                            <option value="designer">Designer</option>
                            {userRole === 'owner' && (
                              <option value="administrator">Administrator</option>
                            )}
                          </select>
                          <button
                            className="remove-btn"
                            onClick={() =>
                              handleRemoveMember(member.user_id, member.full_name || member.email)
                            }
                            disabled={member.role_code === 'administrator' && userRole !== 'owner'}>
                            Remove
                          </button>
                        </div>
                      )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'settings' && hasPermission('org_manage_settings') && (
          <div className="settings-tab">
            <h3>Organization Settings</h3>
            <form onSubmit={handleUpdateOrganization} className="settings-form">
              <div className="form-group">
                <label>Organization Name</label>
                <input
                  type="text"
                  value={orgForm.name}
                  onChange={(e) => setOrgForm((prev) => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={orgForm.description}
                  onChange={(e) => setOrgForm((prev) => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  placeholder="Optional description of your organization"
                />
              </div>
              <div className="form-group">
                <label>Website URL</label>
                <input
                  type="url"
                  value={orgForm.website}
                  onChange={(e) => setOrgForm((prev) => ({ ...prev, website: e.target.value }))}
                  placeholder="https://example.com"
                />
              </div>

              <div className="form-group">
                <label>Organization Logo</label>
                <p style={{ fontSize: '0.85rem', color: '#6c757d', margin: '0 0 0.75rem 0' }}>
                  This logo will appear as the default brand on all surveys in this organization.
                </p>
                {orgForm.logoUrl && (
                  <div
                    style={{
                      marginBottom: '0.75rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                    }}>
                    <img
                      src={orgForm.logoUrl}
                      alt="Organization Logo"
                      style={{
                        height: 60,
                        maxWidth: 200,
                        objectFit: 'contain',
                        borderRadius: '8px',
                        border: '1px solid #dee2e6',
                        padding: '4px',
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none'
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleRemoveLogo}
                      style={{
                        padding: '0.25rem 0.75rem',
                        background: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                      }}>
                      Remove
                    </button>
                  </div>
                )}
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                  <input
                    type="url"
                    value={orgForm.logoUrl}
                    onChange={(e) => setOrgForm((prev) => ({ ...prev, logoUrl: e.target.value }))}
                    placeholder="https://example.com/logo.png"
                    style={{ flex: 1 }}
                  />
                  <span style={{ color: '#6c757d', fontSize: '0.85rem' }}>or</span>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleLogoFileUpload}
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={logoUploading}
                    style={{
                      padding: '0.5rem 1rem',
                      background: '#0D9488',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      fontSize: '0.9rem',
                    }}>
                    {logoUploading ? 'Uploading...' : 'Upload Image'}
                  </button>
                </div>
                <p style={{ margin: '0.375rem 0 0 0', fontSize: '0.75rem', color: '#999' }}>
                  Max 5 MB · JPEG, PNG, WebP, or GIF
                </p>
              </div>

              <button type="submit" className="save-btn">
                Save Changes
              </button>
            </form>
          </div>
        )}
      </div>

      <style>{`
        .org-settings {
          max-width: 1000px;
          margin: 0 auto;
          padding: 2rem;
        }

        .org-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          padding-bottom: 1rem;
          border-bottom: 2px solid #eee;
        }

        .org-header h1 {
          margin: 0;
          color: #333;
        }

        .plan-badge {
          display: inline-block;
          padding: 0.3rem 0.9rem;
          border-radius: 9999px;
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          color: var(--accent-color);
          background: rgba(13, 148, 136, 0.06);
          border: 1.5px solid var(--accent-color);
        }

        .error-message {
          background-color: #f8d7da;
          color: #721c24;
          padding: 0.75rem;
          border-radius: 4px;
          margin-bottom: 1rem;
        }

        .success-message {
          background-color: #d1edff;
          color: #0c5460;
          padding: 0.75rem;
          border-radius: 4px;
          margin-bottom: 1rem;
        }

        .org-tabs {
          display: flex;
          border-bottom: 2px solid #eee;
          margin-bottom: 2rem;
        }

        .org-tabs button {
          padding: 0.75rem 1.5rem;
          border: none;
          background: none;
          cursor: pointer;
          font-size: 1rem;
          border-bottom: 3px solid transparent;
          transition: all 0.2s;
          color: #495057;
          font-weight: 500;
        }

        .org-tabs button:hover {
          background-color: #f8f9fa;
          color: #212529;
        }

        .org-tabs button.active {
          border-bottom-color: #0d6efd;
          color: #0d6efd;
          font-weight: bold;
        }

        .org-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .stat-card {
          background: #f8f9fa;
          padding: 1.5rem;
          border-radius: 8px;
          text-align: center;
        }

        .stat-card h3 {
          margin: 0 0 0.5rem 0;
          color: #6c757d;
          font-size: 0.9rem;
          text-transform: uppercase;
        }

        .stat-card p {
          margin: 0;
          font-size: 1.5rem;
          font-weight: bold;
          color: #333;
        }

        .org-info {
          background: white;
          border: 1px solid #dee2e6;
          border-radius: 8px;
          padding: 1.5rem;
        }

        .org-info h3 {
          margin: 0 0 1rem 0;
          color: #333;
        }

        .info-grid {
          display: grid;
          gap: 0.75rem;
        }

        .info-grid div {
          padding: 0.5rem 0;
          border-bottom: 1px solid #f8f9fa;
        }

        .info-grid a {
          color: #0d6efd;
          text-decoration: none;
          margin-left: 0.5rem;
        }

        .invite-section {
          background: white;
          border: 1px solid #dee2e6;
          border-radius: 8px;
          padding: 1.5rem;
          margin-bottom: 2rem;
        }

        .invite-form .form-group {
          display: flex;
          gap: 1rem;
          align-items: center;
        }

        .invite-form input,
        .invite-form select {
          padding: 0.5rem;
          border: 1px solid #dee2e6;
          border-radius: 4px;
          font-size: 1rem;
        }

        .invite-form input {
          flex: 2;
        }

        .invite-form select {
          flex: 1;
        }

        .invite-form button {
          padding: 0.5rem 1rem;
          background-color: #0d6efd;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 1rem;
        }

        .invite-form button:disabled {
          background-color: #6c757d;
          cursor: not-allowed;
        }

        .members-section {
          background: white;
          border: 1px solid #dee2e6;
          border-radius: 8px;
          padding: 1.5rem;
          margin-bottom: 2rem;
        }

        .member-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1rem;
          border-bottom: 1px solid #f8f9fa;
        }

        .member-item:last-child {
          border-bottom: none;
        }

        .member-name {
          font-weight: bold;
          color: #333;
        }

        .member-email {
          font-size: 0.9rem;
          color: #6c757d;
        }

        .member-meta {
          font-size: 0.8rem;
          color: #6c757d;
        }

        .role-badge {
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: bold;
          color: white;
        }

        .member-actions {
          display: flex;
          gap: 0.5rem;
          align-items: center;
        }

        .member-actions select {
          padding: 0.25rem;
          border: 1px solid #dee2e6;
          border-radius: 4px;
          font-size: 0.85rem;
        }

        .remove-btn {
          padding: 0.25rem 0.75rem;
          background-color: #dc3545;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.85rem;
        }

        .settings-form {
          background: white;
          border: 1px solid #dee2e6;
          border-radius: 8px;
          padding: 1.5rem;
        }

        .settings-form .form-group {
          margin-bottom: 1rem;
        }

        .settings-form label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: bold;
          color: #333;
        }

        .settings-form input,
        .settings-form textarea {
          width: 100%;
          padding: 0.5rem;
          border: 1px solid #dee2e6;
          border-radius: 4px;
          font-size: 1rem;
        }

        .save-btn {
          padding: 0.75rem 1.5rem;
          background-color: #198754;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 1rem;
        }

        .no-org {
          text-align: center;
          padding: 4rem 2rem;
          color: #6c757d;
        }

        .loading {
          text-align: center;
          padding: 2rem;
          color: #6c757d;
        }

        .invite-hint {
          font-size: 0.9rem;
          color: #6c757d;
          margin-bottom: 1rem;
        }

        .pending-section {
          background-color: #fff8e6;
          border-color: #ffc107;
        }

        .pending-item {
          background-color: rgba(255, 193, 7, 0.05);
        }

        .pending-email {
          font-style: italic;
        }

        .pending-badge {
          display: inline-block;
          padding: 0.15rem 0.5rem;
          background-color: #ffc107;
          color: #856404;
          border-radius: 4px;
          font-size: 0.7rem;
          font-weight: bold;
          text-transform: uppercase;
        }

        .revoke-btn {
          padding: 0.25rem 0.75rem;
          background-color: #6c757d;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.85rem;
        }

        .revoke-btn:hover {
          background-color: #5a6268;
        }
      `}</style>
    </div>
  )
}

export default OrganizationSettings
